import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";

import { Business, Check, Close, Delete as DeleteIcon, Download, Person } from "@mui/icons-material";
import { Avatar, Chip, Divider, IconButton, Skeleton, Slide, Tooltip, Typography, useMediaQuery, useTheme } from "@mui/material";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { socket } from "../sockets";
import { deleteMyConnections, EConnectionStatus, getMyConnections, IRoles, patchStaffType, updateMyConnections } from "../utils";
import { BaseDialog } from "./common/BaseDialog";
import { LoaderAnimationComponent } from "./common/LoaderAnimationComponent";
import { SnackbarAlert } from "./common/SnackbarAlert";
import { colors, roleColors, alphaVar } from "./ui/colors";
import { selectRole } from "../redux/features/user/selectors/userState.selectors";

function EnhancedTableHead(props) {
  const { order, orderBy, onRequestSort } = props;
  const userRole = useSelector(selectRole);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const GRAY = colors.gray;
  const ORANGE = colors.orange;

  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  const headCells = [
    { id: "date", label: "Date", style: { maxWidth: 130 } },
    {
      id: userRole === IRoles.Employee ? "company" : "cleaner",
      label: userRole === IRoles.Employee ? "Company" : "Cleaner",
      style: { minWidth: 180 },
    },
    ...(userRole === IRoles.Business || userRole === IRoles.Other ? [{ id: "contact", label: "Contact Info", style: { minWidth: 200 } }] : []),
    { id: "typeOfJob", label: "Job Type", style: { maxWidth: 130 } },
    { id: "typeOfService", label: "Services", style: { maxWidth: 140 } },
    { id: "requirements", label: "Requirements", style: { maxWidth: 140 } },
    { id: "Assigned Role", label: "Staff Type", style: { maxWidth: 140 } },
    { id: "status", label: "Status", style: { minWidth: 130 } },
    ...(userRole === IRoles.Employee ? [{ id: "actions", label: "", style: { width: 56 } }] : []),
  ];

  return (
    <TableHead>
      <TableRow
        sx={{
          backgroundColor: "var(--fx-surface-base, #FFFFFF)",
          "& th": {
            fontWeight: 700,
            color: GRAY[800],
            borderBottom: `2px solid ${ORANGE.base}`,
            fontSize: isMobile ? "0.75rem" : "0.875rem",
            backgroundColor: "var(--fx-surface-base, #FFFFFF)",
          },
        }}
      >
        {headCells.map((headCell) => (
          <TableCell key={headCell.id} align="left" sortDirection={orderBy === headCell.id ? order : false} sx={headCell.style}>
            {headCell.label ? (
              <TableSortLabel
                sx={{
                  color: `${GRAY[500]} !important`,
                  fontWeight: 600,
                  "&:hover": { color: `${GRAY[700]} !important` },
                  "& .MuiTableSortLabel-icon": { color: `${GRAY.base} !important` },
                }}
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : "ASC"}
                onClick={createSortHandler(headCell.id)}
              >
                {headCell.label}
              </TableSortLabel>
            ) : null}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.oneOf(["ASC", "DESC"]).isRequired,
  orderBy: PropTypes.string.isRequired,
};

export const ConnectionRequests = ({ sx, showTitle = true }) => {
  const token = sessionStorage.getItem("token");
  const [order, setOrder] = useState("DESC");
  const [orderBy, setOrderBy] = useState("date");
  const [page, setPage] = useState(0);
  const [totalRquest, setTotalRequests] = useState(0);
  const [open, setOpen] = useState(false);
  const [openDecline, setOpenDecline] = useState(false);
  const [index, setIndex] = useState(0);
  const [requests, setRequests] = useState([]);
  const [pendingConnections, setPendingConnections] = useState([]);
  const [openLoader, setOpenLoader] = useState(false);
  const [rows, setRows] = useState([]);
  const [refresh, setRefresh] = useState(false);

  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [pageSize, setPageSize] = useState(isXs ? 8 : 12);
  const [rowsPerPage, setRowsPerPage] = useState(isXs ? [4, 8, 12] : [15, 25, 35]);

  const [openAlert, setOpenAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("");

  const userRole = useSelector(selectRole)
  const company = useSelector((state) => state.userState.company);

  const GRAY = colors.gray;
  const ORANGE = colors.orange;

  const headers = ["Date", "Cleaner", "Phone", "Address", "Email", "Type of Job", "Type of Service", "Requirements", "Status"];

  useEffect(() => {
    socket.on("connectionState", () => setRefresh((prev) => !prev));
  }, []);

  useEffect(() => {
    if (!company?.id) return;

    setOpenLoader(true);

    getMyConnections({
      companyId: company.id,
      page: page + 1,
      pageSize,
      token,
    })
      .then((res) => {
        const items = res?.items ?? [];

        setRows(items);
        setRequests(items);
        setTotalRequests(res?.totalItems ?? 0);

        setPendingConnections(
          items.map((item) => ({
            status: item.status,
            isVisible: true,
            id: item.id,
          })),
        );
      })
      .finally(() => setOpenLoader(false));
  }, [page, pageSize, company?.id, token, refresh]);

  const handleChangeRole = async (uid, staffType) => {
    try {
      setRefresh((v) => !v);
      await patchStaffType({ token, uid, staffType });
    } catch (error) {
      console.log(error);
    }
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "ASC";
    setOrder(isAsc ? "DESC" : "ASC");
    setOrderBy(property);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleConnectionsSlide = () => {
    setPendingConnections((prev) => prev.map((item, i) => (i === index ? { ...item, isVisible: false } : item)));
  };

  const exportDataToCsv = () => {
    const dataRows = requests?.map((row) => [
      row?.createdAt,
      row?.user?.firstName + "  " + row?.user?.lastName || "No information found",
      row?.user?.phone || "No information found",
      row?.user?.address || "No information found",
      row?.user?.email || "No information found",
      row?.workTypesServices[0]?.workTypes?.name || "No information found",
      row?.workTypesServices && row?.workTypesServices.length > 0
        ? `"${row?.workTypesServices?.map((s) => s?.workServices?.name).join(" , ")}"`
        : "No information found",
      row.workRequirements && row.workRequirements.length > 0
        ? `"${row?.workTypesServices?.map((s) => s?.workServices?.name).join(" , ")}"`
        : "No information found",
      row.status,
    ]);
    const csvData = [headers, ...dataRows];
    const csvContent = csvData?.map((r) => r.join(",")).join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.download = "My Connections.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleOpenConfirm = (i) => {
    setIndex(i);
    setOpen(true);
  };
  const handleCloseConfirm = () => setOpen(false);

  const handleAcceptRequest = async () => {
    setOpenLoader(true);
    const item = pendingConnections[index];
    const id = item.id;
    setOpen(false);

    updateMyConnections(id, "accepted", token)
      .then(() => {
        setPendingConnections((prev) => prev?.map((it, i) => (i === index ? { ...it, status: "accepted" } : it)));
        setAlertMessage("You've accepted the connection! The company will reach out to you shortly, within a maximum of 48 hours");
        setAlertSeverity("success");
        handleConnectionsSlide();
      })
      .catch((error) => {
        setAlertMessage("The connection has expired");
        if (error?.response?.data?.status === 400 || error?.response?.data?.status === 404) setAlertSeverity("warning");
        else setAlertSeverity("error");
        setRefresh((v) => !v);
      })
      .finally(() => {
        setOpenLoader(false);
        setOpenAlert(true);
      });
  };

  const handleOpenDecline = (i) => {
    setIndex(i);
    setOpenDecline(true);
  };
  const handleCloseDecline = () => setOpenDecline(false);

  const handleDeclineRequest = async () => {
    setOpenLoader(true);
    const item = pendingConnections[index];
    const id = item.id;
    setOpenDecline(false);

    updateMyConnections(id, "declined", token)
      .then(() => {
        setAlertMessage("You've declined the connection");
        setAlertSeverity("info");
        setPendingConnections((prev) => prev?.map((it, i) => (i === index ? { ...it, status: "declined" } : it)));
        handleConnectionsSlide();
      })
      .catch((error) => {
        setAlertMessage("The connection has expired");
        if (error?.response?.data?.status === 400 || error?.response?.data?.status === 404) setAlertSeverity("warning");
        else setAlertSeverity("error");
        setRefresh((v) => !v);
      })
      .finally(() => {
        setOpenLoader(false);
        setOpenAlert(true);
      });
  };

  // Trash action: remove/cancel pending connections (Employee only)
  const handleDeletePending = async (rowIndex) => {
    const item = pendingConnections[rowIndex];
    const id = item?.id;
    if (!id) return;

    setOpenLoader(true);

    deleteMyConnections(id)
      .then(() => {
        setAlertMessage("Pending connection removed");
        setAlertSeverity("info");

        setPendingConnections((prev) => prev.map((it, i) => (i === rowIndex ? { ...it, status: "canceled", isVisible: false } : it)));

        // refresh list
        setRefresh((v) => !v);
      })
      .catch(() => {
        setAlertMessage("Could not remove pending connection");
        setAlertSeverity("error");
      })
      .finally(() => {
        setOpenLoader(false);
        setOpenAlert(true);
      });
  };

  const getStatusColor = (status) => {
    const s = (status || "").toLowerCase();
    if (s === "accepted") return { color: "#10b981", bg: alphaVar("#10b981", 0.1) };
    if (s === "declined") return { color: "#ef4444", bg: alphaVar("#ef4444", 0.1) };
    if (s === "pending") return { color: "#f59e0b", bg: alphaVar("#f59e0b", 0.1) };
    if (s === "canceled") return { color: "#8b5cf6", bg: alphaVar("#8b5cf6", 0.1) };
    return { color: GRAY[600], bg: alphaVar(GRAY[600], 0.1) };
  };

  const renderContactInfo = (row) => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography variant="caption" sx={{ color: GRAY[500], minWidth: 45 }}>
          Phone:
        </Typography>
        <Typography variant="body2" sx={{ color: GRAY[700] }}>
          {row?.user?.phone || "N/A"}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography variant="caption" sx={{ color: GRAY[500], minWidth: 45 }}>
          Email:
        </Typography>
        <Typography variant="body2" sx={{ color: GRAY[700], wordBreak: "break-word" }}>
          {row?.user?.email || "N/A"}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography variant="caption" sx={{ color: GRAY[500], minWidth: 45 }}>
          Address:
        </Typography>
        <Typography variant="body2" sx={{ color: GRAY[700] }}>
          {row?.user?.address || "N/A"}
        </Typography>
      </Box>
    </Box>
  );

  if (!rows?.length && !openLoader) {
    return (
      <Box sx={{ width: "100%", display: "flex", p: 2, flexDirection: "column", ...sx }}>
        {showTitle && (
          <Typography variant={isMobile ? "h6" : "h5"} sx={{ mb: 2, fontWeight: 700, color: GRAY[900] }}>
            Connection Requests
          </Typography>
        )}
        <Paper
          sx={{
            p: 4,
            textAlign: "center",
            border: `1px solid rgba(12,12,12,.06)`,
            borderRadius: 2,
            boxShadow: "0 2px 8px rgba(12,12,12,.04)",
          }}
        >
          <Typography variant="body1" sx={{ color: GRAY[600] }}>
            There are currently no connection requests.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", height: "100%", minHeight: 0, ...sx }}>
      {showTitle && (
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, ml: 2 }}>
          <Typography variant={isMobile ? "h6" : "h5"} sx={{ fontWeight: 700, color: GRAY[900] }}>
            Connection Requests
          </Typography>
          {userRole !== IRoles.Employee && (
            <Tooltip title="Export to CSV">
              <IconButton
                onClick={exportDataToCsv}
                sx={{
                  color: ORANGE.base,
                  "&:hover": { backgroundColor: alphaVar(ORANGE.base, 0.08) },
                }}
              >
                <Download />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}

      <TableContainer
        component={Paper}
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          height: "100%",
          minHeight: 0,
          border: `1px solid rgba(12,12,12,.06)`,
          backgroundColor: "var(--fx-surface-base, #FFFFFF)",
          borderRadius: 2,
          boxShadow: "0 2px 8px rgba(12,12,12,.04)",
          overflow: "hidden",
        }}
      >
        <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
          <Table stickyHeader size={isMobile ? "small" : "medium"}>
            <EnhancedTableHead order={order} orderBy={orderBy} onRequestSort={handleRequestSort} />
            <TableBody>
              {openLoader
                ? new Array(10).fill(null).map((_, idx) => (
                  <TableRow key={idx}>
                    <TableCell colSpan={userRole.type === IRoles.Business || userRole.type === IRoles.Other ? 8 : 7}>
                      <Skeleton variant="rectangular" height={60} />
                    </TableCell>
                  </TableRow>
                ))
                : rows?.map((row, idx) => (
                  <TableRow
                    key={row.id}
                    sx={{
                      "& > *": { borderBottom: "unset" },
                      cursor: "pointer",
                      backgroundColor: "var(--fx-surface-base, #FFFFFF)",
                      borderLeft: "3px solid transparent",
                      transition: "background-color 140ms ease, border-color 140ms ease",
                      "&:hover": {
                        backgroundColor: "var(--fx-surface-sunken, #F8FAFC)",
                        borderLeftColor: ORANGE[200],
                      },
                    }}
                  >
                    {/* Date */}
                    <TableCell>
                      <Typography variant="body2" sx={{ color: GRAY[700], fontWeight: 500 }}>
                        {dayjs(row.createdAt).format("MM/DD/YYYY")}
                      </Typography>
                    </TableCell>

                    {/* Company/Cleaner */}
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Avatar
                          sx={{
                            width: isMobile ? 32 : 40,
                            height: isMobile ? 32 : 40,
                            backgroundColor: "var(--fx-surface-base, #FFFFFF)",
                            color: userRole.type === IRoles.Employee ? roleColors.commercial.base : roleColors.residential.base,
                            fontWeight: 700,
                            border: `1px solid ${userRole.type === IRoles.Employee ? roleColors.commercial[100] : roleColors.residential[100]}`,
                          }}
                        >
                          {userRole.type === IRoles.Employee ? <Business fontSize="small" /> : <Person fontSize="small" />}
                        </Avatar>
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 600, color: GRAY[800], lineHeight: 1.2 }}>
                            {userRole.type === IRoles.Employee ? row?.company?.businessName : `${row?.user?.firstName} ${row?.user?.lastName}`}
                          </Typography>
                          <Typography variant="caption" sx={{ color: GRAY[500] }}>
                            {userRole.type === IRoles.Employee ? "Business" : "Individual"}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>

                    {/* Contact Info (Business only) */}
                    {(userRole.type === IRoles.Business || userRole.type === IRoles.Other) && <TableCell>{renderContactInfo(row)}</TableCell>}

                    {/* Job Type */}
                    <TableCell>
                      <Chip
                        label={row?.workTypesServices?.[0]?.workTypes?.name || "N/A"}
                        size="small"
                        sx={{
                          backgroundColor: alphaVar(ORANGE.base, 0.1),
                          color: ORANGE[700],
                          fontWeight: 500,
                        }}
                      />
                    </TableCell>

                    {/* Services */}
                    <TableCell sx={{ maxWidth: 200 }}>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                        {row?.workTypesServices && row?.workTypesServices.length > 0 ? (
                          row?.workTypesServices.slice(0, 2).map((item, i2) => (
                            <Typography key={i2} variant="body2" sx={{ color: GRAY[600] }}>
                              • {item?.workServices?.name}
                            </Typography>
                          ))
                        ) : (
                          <Typography variant="body2" sx={{ color: GRAY[500] }}>
                            N/A
                          </Typography>
                        )}
                        {row?.workTypesServices?.length > 2 && (
                          <Typography variant="caption" sx={{ color: GRAY[500] }}>
                            +{row.workTypesServices.length - 2} more
                          </Typography>
                        )}
                      </Box>
                    </TableCell>

                    {/* Requirements */}
                    <TableCell sx={{ maxWidth: 200 }}>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                        {row?.workRequirements && row?.workRequirements.length > 0 ? (
                          row?.workRequirements.slice(0, 2).map((item, i2) => (
                            <Typography key={i2} variant="body2" sx={{ color: GRAY[600] }}>
                              • {item.name}
                            </Typography>
                          ))
                        ) : (
                          <Typography variant="body2" sx={{ color: GRAY[500] }}>
                            N/A
                          </Typography>
                        )}
                        {row?.workRequirements?.length > 2 && (
                          <Typography variant="caption" sx={{ color: GRAY[500] }}>
                            +{row.workRequirements.length - 2} more
                          </Typography>
                        )}
                      </Box>
                    </TableCell>

                    {/* Workforce Type */}
                    <TableCell>
                      {row?.status === "accepted" && row?.user?.workforces?.[0]?.workforceRole ? (
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            px: 1,
                            py: 0.25,
                            borderRadius: 999,
                            fontSize: "0.75rem",
                            fontWeight: 600,
                            textTransform: "capitalize",
                            color: "primary.main",
                            backgroundColor: "rgba(25, 118, 210, 0.08)",
                            lineHeight: 1,
                          }}
                        >
                          {row.user.workforces[0].workforceRole}
                        </Box>
                      ) : (
                        <Typography variant="body2" color={GRAY[400]}>
                          —
                        </Typography>
                      )}
                    </TableCell>

                    {/* Status */}
                    <TableCell>
                      {userRole.type === IRoles.Employee && row.status === EConnectionStatus.PENDING ? (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, alignItems: "center" }}>
                          {pendingConnections[idx]?.isVisible ? (
                            <>
                              <Tooltip title="Accept connection">
                                <IconButton
                                  onClick={() => handleOpenConfirm(idx)}
                                  size="small"
                                  sx={{
                                    backgroundColor: alphaVar("#10b981", 0.1),
                                    color: "#10b981",
                                    border: "1px solid #10b981",
                                    borderRadius: 1,
                                    px: 1,
                                    fontSize: "0.75rem",
                                    "&:hover": { backgroundColor: alphaVar("#10b981", 0.2) },
                                  }}
                                >
                                  <Check sx={{ fontSize: 14, mr: 0.5 }} />
                                  Accept
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Decline connection">
                                <IconButton
                                  onClick={() => handleOpenDecline(idx)}
                                  size="small"
                                  sx={{
                                    backgroundColor: alphaVar("#ef4444", 0.1),
                                    color: "#ef4444",
                                    border: "1px solid #ef4444",
                                    borderRadius: 1,
                                    px: 1,
                                    fontSize: "0.75rem",
                                    "&:hover": { backgroundColor: alphaVar("#ef4444", 0.2) },
                                  }}
                                >
                                  <Close sx={{ fontSize: 14, mr: 0.5 }} />
                                  Decline
                                </IconButton>
                              </Tooltip>
                            </>
                          ) : (
                            <Slide direction="left" in={!pendingConnections[idx]?.isVisible} mountOnEnter unmountOnExit>
                              <Chip
                                label={pendingConnections[idx]?.status.charAt(0).toUpperCase() + pendingConnections[idx]?.status.slice(1)}
                                size="small"
                                sx={{
                                  backgroundColor: getStatusColor(pendingConnections[idx]?.status).bg,
                                  color: getStatusColor(pendingConnections[idx]?.status).color,
                                  fontWeight: 600,
                                }}
                              />
                            </Slide>
                          )}
                        </Box>
                      ) : (
                        <Chip
                          label={row.status === EConnectionStatus.CANCELED ? "Expired" : row.status.charAt(0).toUpperCase() + row.status.slice(1)}
                          size="small"
                          sx={{
                            backgroundColor: getStatusColor(row.status).bg,
                            color: getStatusColor(row.status).color,
                            fontWeight: 600,
                          }}
                        />
                      )}
                    </TableCell>

                    {/* Actions (trash) */}
                    {(userRole.type === IRoles.Business || userRole.type === IRoles.Other) && (
                      <TableCell align="right">
                        {row.status === EConnectionStatus.PENDING && (
                          <Tooltip title="Remove pending connection">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeletePending(idx);
                              }}
                              sx={{
                                color: GRAY[500],
                                "&:hover": {
                                  backgroundColor: alphaVar("#ef4444", 0.12),
                                  color: "#ef4444",
                                },
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </Box>

        <Divider sx={{ borderColor: "rgba(var(--fx-surface-textrgb, 15, 23, 42), .06)" }} />

        <Box
          sx={{
            position: "sticky",
            bottom: 0,
            zIndex: (theme) => theme.zIndex.appBar + 1,
            backgroundColor: "var(--fx-surface-base, #FFFFFF)",
            borderTop: "1px solid rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.06)",
          }}
        >
          <TablePagination
            rowsPerPageOptions={rowsPerPage}
            component="div"
            count={totalRquest}
            rowsPerPage={pageSize}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            showFirstButton
            showLastButton
            SelectProps={{ MenuProps: { sx: { zIndex: 20000 } } }}
            sx={{
              backgroundColor: "var(--fx-surface-base, #FFFFFF)",
              ".MuiTablePagination-toolbar": { minHeight: 56 },
              ".MuiTablePagination-displayedRows, .MuiTablePagination-selectLabel": {
                color: GRAY[600],
              },
              ".MuiIconButton-root": {
                color: ORANGE.base,
                "&:hover": { backgroundColor: alphaVar(ORANGE.base, 0.08) },
              },
            }}
          />
        </Box>
      </TableContainer>

      <BaseDialog
        openState={open}
        setOpenState={handleCloseConfirm}
        title="Confirm Connection Request"
        titleSx={{ textAlign: "center", fontWeight: 600 }}
        content={`Do you accept the connection request from ${requests[index]?.company?.businessName}?`}
        actionSx={{ justifyContent: "center" }}
        actions={[
          {
            label: "Accept",
            style: {
              backgroundColor: "#10b981",
              color: "white",
              "&:hover": { backgroundColor: "#059669" },
              margin: "0 8px",
              fontWeight: 600,
            },
            onClick: handleAcceptRequest,
          },
          {
            label: "Cancel",
            style: {
              backgroundColor: GRAY[500],
              color: "white",
              "&:hover": { backgroundColor: GRAY[600] },
              margin: "0 8px",
            },
            onClick: handleCloseConfirm,
          },
        ]}
      />

      <BaseDialog
        openState={openDecline}
        setOpenState={handleCloseDecline}
        title="Connection Request Confirmation"
        titleSx={{ textAlign: "center", fontWeight: 600 }}
        content={`Do you confirm to reject the request from ${requests[index]?.company?.businessName}?`}
        actionSx={{ justifyContent: "center" }}
        actions={[
          {
            label: "Decline",
            style: {
              backgroundColor: "#ef4444",
              color: "white",
              "&:hover": { backgroundColor: "#dc2626" },
              margin: "0 8px",
              fontWeight: 600,
            },
            onClick: handleDeclineRequest,
          },
          {
            label: "Cancel",
            style: {
              backgroundColor: GRAY[500],
              color: "white",
              "&:hover": { backgroundColor: GRAY[600] },
              margin: "0 8px",
            },
            onClick: handleCloseDecline,
          },
        ]}
      />

      <SnackbarAlert openState={openAlert} setOpenState={setOpenAlert} severity={alertSeverity} duration={7000} message={alertMessage} />

      <LoaderAnimationComponent open={openLoader} fontSize="large" />
    </Box>
  );
};
